import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { useAuth } from '../../../context/AuthContext';
import { useTimezone } from '../../../hooks/useTimezone';
import { formatPaisa, toPaisa } from '../../../utils/paisa';
import { ToneChip } from '../../common/StatusChip';
import { SelectOption } from '../../common/SearchableSelect';
import JournalLinesEditor, { blankLine, DraftLine } from './JournalLinesEditor';

/**
 * Enter a manual journal — corrections, accruals, opening balances. It must
 * balance before it can be sent; the server checks everything again
 * (accounts, open month) and either posts it or queues it for approval.
 */
const ManualJournalDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  accountOptions: SelectOption[];
  requiresApproval: boolean;
}> = ({ open, onClose, onSaved, accountOptions, requiresApproval }) => {
  const { currency } = useAuth();
  const { getToday } = useTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const [entryDate, setEntryDate] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEntryDate(getToday());
    setMemo('');
    setLines([blankLine(), blankLine()]);
    setError(null);
  }, [open, getToday]);

  const totals = useMemo(() => {
    const filled = lines.filter(l => l.accountId || l.debit || l.credit);
    const debit = filled.reduce((s, l) => s + (toPaisa(l.debit || 0) ?? 0), 0);
    const credit = filled.reduce((s, l) => s + (toPaisa(l.credit || 0) ?? 0), 0);
    const complete = filled.length >= 2 && filled.every(l => l.accountId && (Number(l.debit) > 0 || Number(l.credit) > 0));
    return { filled, debit, credit, complete };
  }, [lines]);

  const balanced = totals.debit > 0 && totals.debit === totals.credit;
  const canSubmit = !busy && !!entryDate && !!memo.trim() && totals.complete && balanced;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await financeService.createManualJournal({
        entryDate,
        memo: memo.trim(),
        lines: totals.filled.map(l => ({
          accountId: l.accountId,
          debitPaisa: toPaisa(l.debit || 0) ?? 0,
          creditPaisa: toPaisa(l.credit || 0) ?? 0,
          description: l.description.trim() || null,
        })),
      });
      enqueueSnackbar(res.entry ? `Posted as ${res.entry.entryNo}` : 'Sent for approval', { variant: 'success' });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'The journal could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const difference = Math.abs(totals.debit - totals.credit);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        New journal
        <Typography variant="body2" color="text.secondary">
          Debits must equal credits. {requiresApproval ? 'Someone else approves it before it posts.' : 'It posts as soon as you save it.'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            label="Date"
            type="date"
            size="small"
            value={entryDate}
            onChange={e => setEntryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: getToday() }}
            disabled={busy}
            sx={{ width: { sm: 180 } }}
          />
          <TextField
            label="What is this journal for?"
            size="small"
            value={memo}
            onChange={e => setMemo(e.target.value)}
            inputProps={{ maxLength: 500 }}
            disabled={busy}
            fullWidth
          />
        </Stack>

        <JournalLinesEditor lines={lines} onChange={setLines} accountOptions={accountOptions} currency={currency} disabled={busy} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={3}>
          {[['Debits', totals.debit], ['Credits', totals.credit]].map(([label, value]) => (
            <Box key={label as string} sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {currency} {formatPaisa(value as number)}
              </Typography>
            </Box>
          ))}
          <ToneChip
            tone={balanced ? 'success' : totals.debit || totals.credit ? 'warning' : 'neutral'}
            label={balanced ? 'Balanced' : totals.debit || totals.credit ? `Out by ${currency} ${formatPaisa(difference)}` : 'Enter amounts'}
          />
        </Stack>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!canSubmit}>
          {busy ? 'Saving…' : requiresApproval ? 'Send for approval' : 'Post journal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualJournalDialog;
